/**
 * lbx-training.ts — browser SDK for the Linkbox AI training-data ingestion
 * service. Captures audio + facial blendshapes + head pose from the user's
 * mic/camera (with explicit consent) and posts ≤30s chunks to the
 * wardrobe-platform `/api/v1/training/ingest` endpoint.
 *
 * Drop-in for the Linkbox AI web app. The same TS file works inside
 * iOS WKWebView and Android WebView hosts, so this slice handles three
 * surfaces at once.
 *
 * Dependencies (CDN-friendly, all Apache-2.0 / MIT):
 *   @mediapipe/tasks-vision  — FaceLandmarker that outputs 52 ARKit-named blendshapes
 *
 * Usage:
 *   const t = new LbxTrainingCapture({
 *     ingestUrl: '/api/v1/training/ingest',
 *     householdId: 'h-abc',
 *     memberId:    'm-xyz',
 *     platform:    'web',
 *     getConsent:  () => ({ audio: true, camera: true, training: true,
 *                           version: 'v1-2026-05-16' }),
 *   });
 *   await t.startSession({ agentId: 'concierge', context: 'voice_query', lang: 'en-US' });
 *   await t.startCapture();                  // begins chunked uploads
 *   // ... user talks to the agent ...
 *   await t.stopCapture();
 *   await t.endSession();
 */

type ConsentGrants = {
  audio: boolean; camera: boolean; training: boolean; version: string;
  bipaAck?: boolean;
};

type SessionMeta = {
  agentId?: string; context?: string; lang?: string;
};

const CHUNK_SECONDS = 30;
const AUDIO_RATE = 16000;
const FACE_FPS = 30;

export class LbxTrainingCapture {
  ingestUrl: string;
  householdId: string;
  memberId: string;
  platform: "ios" | "android" | "web" | "mac" | "net";
  getConsent: () => ConsentGrants;
  sessionId: string | null = null;

  private audioCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private camStream: MediaStream | null = null;
  private faceLandmarker: any = null;   // MediaPipe's class
  private videoEl: HTMLVideoElement | null = null;

  // Rolling per-chunk buffers
  private chunkSeq = 0;
  private audioBuf: Float32Array[] = [];
  private faceBuf: { t: number; bs: number[] }[] = [];
  private poseBuf: { t: number; rot: number[]; pos: number[] }[] = [];
  private chunkStartT = 0;
  private flushTimer: number | null = null;

  constructor(opts: {
    ingestUrl: string; householdId: string; memberId: string;
    platform: "ios" | "android" | "web" | "mac" | "net";
    getConsent: () => ConsentGrants;
  }) {
    this.ingestUrl = opts.ingestUrl;
    this.householdId = opts.householdId;
    this.memberId = opts.memberId;
    this.platform = opts.platform;
    this.getConsent = opts.getConsent;
  }

  // ── Session lifecycle ──────────────────────────────────────────────
  async startSession(meta: SessionMeta = {}): Promise<string> {
    const form = new FormData();
    form.set("household_id", this.householdId);
    form.set("platform", this.platform);
    if (meta.agentId) form.set("agent_id", meta.agentId);
    if (meta.context) form.set("context", meta.context);
    if (meta.lang)    form.set("lang", meta.lang);
    const res = await fetch(this.ingestUrl.replace("/ingest", "/sessions/start"),
                            { method: "POST", body: form });
    if (!res.ok) throw new Error("startSession: " + res.status);
    const json = await res.json();
    this.sessionId = json.session_id;
    return json.session_id;
  }

  async endSession(): Promise<void> {
    if (!this.sessionId) return;
    await fetch(this.ingestUrl.replace("/ingest", `/sessions/${this.sessionId}/end`),
                { method: "POST" });
    this.sessionId = null;
  }

  // ── Capture lifecycle ──────────────────────────────────────────────
  async startCapture(): Promise<void> {
    const c = this.getConsent();
    if (!c.audio && !c.camera) throw new Error("nothing consented");
    if (!this.sessionId) throw new Error("call startSession() first");

    if (c.audio) {
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioCtx = new AudioContext({ sampleRate: AUDIO_RATE });
      const src = this.audioCtx.createMediaStreamSource(this.micStream);
      const proc = this.audioCtx.createScriptProcessor(1024, 1, 1);
      proc.onaudioprocess = (ev) => {
        const f32 = ev.inputBuffer.getChannelData(0);
        this.audioBuf.push(new Float32Array(f32));
      };
      src.connect(proc);
      proc.connect(this.audioCtx.destination);
    }

    if (c.camera) {
      this.camStream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.videoEl = document.createElement("video");
      this.videoEl.srcObject = this.camStream;
      this.videoEl.muted = true;
      await this.videoEl.play();
      await this._loadMediaPipe();
      this._startFaceLoop();
    }

    this.chunkStartT = performance.now();
    this.flushTimer = window.setInterval(() => this._flushChunk(),
                                          CHUNK_SECONDS * 1000) as unknown as number;
  }

  async stopCapture(): Promise<void> {
    if (this.flushTimer) { clearInterval(this.flushTimer); this.flushTimer = null; }
    await this._flushChunk();  // last partial chunk
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.camStream?.getTracks().forEach((t) => t.stop());
    if (this.audioCtx) { try { this.audioCtx.close(); } catch (_) {} this.audioCtx = null; }
    this.faceLandmarker = null;
    this.videoEl = null;
  }

  // ── MediaPipe FaceLandmarker (lazy-loaded) ─────────────────────────
  private async _loadMediaPipe(): Promise<void> {
    if (this.faceLandmarker) return;
    const vision = await import(
      // @ts-ignore — dynamic import from esm.sh
      "https://esm.sh/@mediapipe/tasks-vision@0.10.20"
    );
    const fileset = await vision.FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.20/wasm",
    );
    this.faceLandmarker = await vision.FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task",
      },
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
      runningMode: "VIDEO",
      numFaces: 1,
    });
  }

  private _startFaceLoop(): void {
    const step = () => {
      if (!this.faceLandmarker || !this.videoEl) return;
      const tMs = performance.now();
      try {
        const res = this.faceLandmarker.detectForVideo(this.videoEl, tMs);
        if (res.faceBlendshapes?.[0]) {
          // MediaPipe returns 52 categories ordered identically to Apple's
          // ARKit-52 (sans "tongueOut" depending on model version; we
          // pad to 52 to keep the schema stable).
          const bs = new Array(52).fill(0);
          res.faceBlendshapes[0].categories.forEach((c: any, i: number) => {
            if (i < 52) bs[i] = c.score;
          });
          this.faceBuf.push({ t: tMs / 1000, bs });
        }
        if (res.facialTransformationMatrixes?.[0]) {
          const m = res.facialTransformationMatrixes[0].data as Float32Array;
          // Column-major 4x4; rotation = upper-left 3x3 → quaternion;
          // position = last column. v1: just store the raw matrix's
          // translation; emit a placeholder identity rotation.
          this.poseBuf.push({
            t: tMs / 1000,
            rot: [1, 0, 0, 0],
            pos: [m[12], m[13], m[14]],
          });
        }
      } catch (_) {/* swallow per-frame failures */}
      if (this.flushTimer !== null) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // ── Chunk flush ────────────────────────────────────────────────────
  private async _flushChunk(): Promise<void> {
    if (this.audioBuf.length === 0 && this.faceBuf.length === 0) return;
    if (!this.sessionId) return;
    const c = this.getConsent();
    if (!c.training) {            // user revoked mid-session — discard, don't post
      this.audioBuf = []; this.faceBuf = []; this.poseBuf = [];
      return;
    }

    // Concatenate audio
    const totalSamples = this.audioBuf.reduce((s, b) => s + b.length, 0);
    const audioPcm = new Float32Array(totalSamples);
    let off = 0;
    for (const b of this.audioBuf) { audioPcm.set(b, off); off += b.length; }
    this.audioBuf = [];
    const audioWavBytes = floatPcmToWav(audioPcm, AUDIO_RATE);

    const arkitJsonl = this.faceBuf.length
      ? this.faceBuf.map((f) => JSON.stringify(f)).join("\n") : "";
    const poseJsonl  = this.poseBuf.length
      ? this.poseBuf.map((p) => JSON.stringify(p)).join("\n") : "";
    this.faceBuf = []; this.poseBuf = [];

    const form = new FormData();
    form.set("session_id", this.sessionId);
    form.set("member_id", this.memberId);
    form.set("seq", String(++this.chunkSeq));
    form.set("platform", this.platform);
    form.set("consent_version", c.version);
    form.set("intended_for_training", String(c.training));
    // @ts-ignore — Uint8Array vs BlobPart lib.dom widening (linkbox-web tsconfig strictness)
    form.set("audio", new Blob([audioWavBytes], { type: "audio/wav" }), "audio.wav");
    if (arkitJsonl) {
      form.set("arkit_52", new Blob([arkitJsonl], { type: "application/jsonl" }), "arkit_52.jsonl");
    }
    if (poseJsonl) {
      form.set("head_pose", new Blob([poseJsonl], { type: "application/jsonl" }), "head_pose.jsonl");
    }

    try {
      const res = await fetch(this.ingestUrl, { method: "POST", body: form });
      if (!res.ok) console.warn("[lbx-training] chunk POST failed:", res.status);
    } catch (e) {
      console.warn("[lbx-training] chunk POST error:", e);
    }
  }
}

// Float32 PCM @ 16 kHz → 16-bit PCM WAV bytes (little-endian).
function floatPcmToWav(samples: Float32Array, rate: number): Uint8Array {
  const n = samples.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const view = new DataView(buf);
  // RIFF header
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + n * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);   // chunk size
  view.setUint16(20, 1, true);    // PCM
  view.setUint16(22, 1, true);    // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, n * 2, true);
  // Samples
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Uint8Array(buf);
}
function writeString(v: DataView, off: number, s: string) {
  for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i));
}
